import type { Time } from "@core/shared/types";
import type { AssessmentDetails, UserAssessment } from "../types";

/** Older API output serialized the clock as ISO instead of the Time object. */
export function assessmentServerTimestamp(
  serverTime: UserAssessment["serverTime"],
) {
  const timestamp =
    typeof serverTime === "string"
      ? Date.parse(serverTime)
      : serverTime?.timestamp;
  return Number.isFinite(timestamp) ? timestamp : undefined;
}

export function assessmentDeadline(
  attempt?: UserAssessment,
  quizEndsAt?: Time,
) {
  const deadlines = [attempt?.endsAt?.timestamp, quizEndsAt?.timestamp].filter(
    (timestamp): timestamp is number => Number.isFinite(timestamp),
  );
  return deadlines.length ? Math.min(...deadlines) : undefined;
}

/** Fresh terminal/new attempts win; only the same active attempt keeps local edits. */
export function restoreActiveAssessmentDraft(
  incoming: AssessmentDetails,
  current?: AssessmentDetails,
) {
  if (
    current?.id !== incoming.id ||
    !incoming.studentQuiz?.id ||
    current?.studentQuiz?.id !== incoming.studentQuiz.id ||
    incoming.studentQuiz.status !== "active" ||
    current.studentQuiz.status !== "active"
  ) {
    return incoming;
  }

  const localAnswers = new Map(
    current.studentQuiz.answers.map((answer) => [answer.id, answer]),
  );
  return {
    ...incoming,
    studentQuiz: {
      ...incoming.studentQuiz,
      answers: incoming.studentQuiz.answers.map((answer) => {
        const local = localAnswers.get(answer.id);
        return local
          ? {
              ...answer,
              answer: local.answer,
              attachment: local.attachment,
              isFlagged: local.isFlagged,
            }
          : answer;
      }),
    },
  };
}

export function assessmentScreen(assessment: AssessmentDetails) {
  if (assessment.locked && assessment.preRequisitesLessons?.length) {
    return "prerequisites";
  }
  const attempt = assessment.studentQuiz;
  switch (attempt?.status) {
    case "active":
      return "questions";
    case "inReview":
    case "submitted":
      return "review";
    case "passed":
    case "failed":
    case "expired":
      return "results";
    case "notStarted":
      return "start";
    default:
      if (!attempt?.isStarted) return "start";
      return attempt.isEvaluated ? "results" : "questions";
  }
}

/** Restore the existing deadline, never a new duration on navigation/reload. */
export function assessmentRemainingSeconds(
  attempt: UserAssessment | undefined,
  durationSeconds: number,
  now = Date.now(),
  quizEndsAt?: Time,
) {
  if (!attempt || attempt.status === "notStarted") return durationSeconds;
  if (
    ["passed", "failed", "expired", "inReview", "submitted"].includes(
      attempt.status,
    )
  ) {
    return 0;
  }
  const deadline = assessmentDeadline(attempt, quizEndsAt);
  if (deadline === undefined) return 0;
  const referenceTime = assessmentServerTimestamp(attempt.serverTime) ?? now;
  return Math.max(0, Math.ceil((deadline - referenceTime) / 1000));
}
